'use client';

import type { Post } from '@routeroyal/theme-core';
import { MarkdownRenderer } from '@routeroyal/ui-core';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Link2,
  Check,
  Mail,
  Share2,
  X,
} from 'lucide-react';

type Props = {
  article: Post;
  relatedArticles: Post[];
  locale: string;
  basePath: string;
  messages: Record<string, unknown>;
};

// ─── Social Icons (inline SVG for crisp rendering at small sizes) ────────────

function XIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function FacebookIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

// ─── Reading Progress Hook ───────────────────────────────────────────────────

function useReadingProgress(contentRef: React.RefObject<HTMLElement | null>) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const el = contentRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const contentTop = rect.top + window.scrollY;
      const contentHeight = el.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY;

      const start = contentTop;
      const end = contentTop + contentHeight - windowHeight;

      if (scrollY <= start) {
        setProgress(0);
      } else if (scrollY >= end) {
        setProgress(100);
      } else {
        setProgress(Math.round(((scrollY - start) / (end - start)) * 100));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [contentRef]);

  return progress;
}

// ─── Sources Section Transformer ─────────────────────────────────────────────

const SOURCES_HEADING_PATTERN = /^(sources|references|bibliography|ressources|further\s+reading|liens\s+utiles|recursos)$/i;

function useSourcesTransform(containerRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const proseEl = container.querySelector('.stone-prose');
    if (!proseEl) return;

    const headings = proseEl.querySelectorAll('h1, h2, h3, h4');

    headings.forEach((heading) => {
      const text = (heading.textContent || '').trim();
      if (!SOURCES_HEADING_PATTERN.test(text)) return;

      // Already transformed
      if (heading.parentElement?.classList.contains('stone-sources-section')) return;

      const wrapper = document.createElement('aside');
      wrapper.className = 'stone-sources-section';
      wrapper.setAttribute('aria-label', text);

      const headerEl = document.createElement('div');
      headerEl.className = 'stone-sources-header';

      const decorLine = document.createElement('span');
      decorLine.className = 'stone-sources-decor';

      const label = document.createElement('span');
      label.className = 'stone-sources-label';
      label.textContent = text;

      headerEl.appendChild(decorLine);
      headerEl.appendChild(label);
      wrapper.appendChild(headerEl);

      const linksContainer = document.createElement('div');
      linksContainer.className = 'stone-sources-links';

      // Collect sibling elements until the next heading
      const siblings: Element[] = [];
      let next = heading.nextElementSibling;
      while (next && !/^H[1-4]$/i.test(next.tagName)) {
        siblings.push(next);
        next = next.nextElementSibling;
      }

      // Extract all links from siblings and create tag-style elements
      let sourceIndex = 0;
      siblings.forEach((sibling) => {
        const links = sibling.querySelectorAll('a');
        if (links.length > 0) {
          links.forEach((link) => {
            sourceIndex++;
            const tag = document.createElement('a');
            tag.href = link.href;
            tag.target = '_blank';
            tag.rel = 'noopener noreferrer';
            tag.className = 'stone-source-tag';

            const num = document.createElement('span');
            num.className = 'stone-source-num';
            num.textContent = String(sourceIndex).padStart(2, '0');

            const name = document.createElement('span');
            name.className = 'stone-source-name';
            name.textContent = link.textContent || '';

            const icon = document.createElement('span');
            icon.className = 'stone-source-icon';
            icon.innerHTML = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;

            tag.appendChild(num);
            tag.appendChild(name);
            tag.appendChild(icon);
            linksContainer.appendChild(tag);
          });
        } else if (sibling.textContent?.trim()) {
          // Plain text source (no link)
          sourceIndex++;
          const tag = document.createElement('span');
          tag.className = 'stone-source-tag stone-source-tag--plain';

          const num = document.createElement('span');
          num.className = 'stone-source-num';
          num.textContent = String(sourceIndex).padStart(2, '0');

          const name = document.createElement('span');
          name.className = 'stone-source-name';
          name.textContent = sibling.textContent.trim();

          tag.appendChild(num);
          tag.appendChild(name);
          linksContainer.appendChild(tag);
        }
      });

      wrapper.appendChild(linksContainer);

      // Replace the heading + siblings with the styled section
      heading.parentNode?.insertBefore(wrapper, heading);
      heading.remove();
      siblings.forEach((s) => s.remove());
    });
  }, [containerRef]);
}

// ─── Share Panel ─────────────────────────────────────────────────────────────

function SharePanel({
  title,
  url,
  isOpen,
  onClose,
  t,
}: {
  title: string;
  url: string;
  isOpen: boolean;
  onClose: () => void;
  t: (key: string, fallback: string) => string;
}) {
  const [copied, setCopied] = useState(false);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  const shareLinks = [
    {
      name: 'X',
      icon: <XIcon size={15} />,
      href: `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon size={15} />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
      name: 'Facebook',
      icon: <FacebookIcon size={15} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      name: 'Email',
      icon: <Mail size={15} />,
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}\n\n${url}`)}`,
    },
  ];

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-500 ${
        isOpen
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`relative bg-stone-50 border border-stone-200 p-10 md:p-14 max-w-md w-full mx-6 transition-all duration-500 ${
          isOpen
            ? 'translate-y-0 scale-100'
            : 'translate-y-8 scale-95'
        }`}
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-stone-400 hover:text-stone-900 transition-colors"
          aria-label={t('closeSharePanel', 'Close share panel')}
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <span className="h-px w-6 bg-stone-400" />
          <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-stone-500">
            {t('share', 'Share')}
          </p>
        </div>
        <h3 className="font-serif text-2xl text-stone-900 italic mb-8">
          {t('spreadTheWord', 'Spread the word')}
        </h3>

        <div className="space-y-3">
          {shareLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 px-5 py-4 border border-stone-200 text-stone-600 hover:border-stone-900 hover:text-stone-900 transition-all duration-300 group"
            >
              <span className="text-stone-400 group-hover:text-stone-900 transition-colors">
                {link.icon}
              </span>
              <span className="font-sans text-xs uppercase tracking-widest">
                {link.name}
              </span>
              <ArrowRight
                size={12}
                className="ml-auto opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
              />
            </a>
          ))}

          {/* Copy Link */}
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-4 px-5 py-4 border border-stone-200 text-stone-600 hover:border-stone-900 hover:text-stone-900 transition-all duration-300 group"
          >
            <span className="text-stone-400 group-hover:text-stone-900 transition-colors">
              {copied ? <Check size={15} /> : <Link2 size={15} />}
            </span>
            <span className="font-sans text-xs uppercase tracking-widest">
              {copied ? t('copied', 'Copied!') : t('copyLink', 'Copy Link')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Floating Side Bar ───────────────────────────────────────────────────────

function FloatingSidebar({
  progress,
  readingTime,
  onShareClick,
  shareLabel,
}: {
  progress: number;
  readingTime: number;
  onShareClick: () => void;
  shareLabel: string;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={`fixed left-6 xl:left-10 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col items-center gap-6 transition-all duration-700 ${
        visible
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 -translate-x-4'
      }`}
    >
      {/* Vertical progress track */}
      <div className="relative w-px h-24 bg-stone-200">
        <div
          className="absolute bottom-0 left-0 w-full bg-stone-900 transition-all duration-300 ease-out"
          style={{ height: `${progress}%` }}
        />
      </div>

      {/* Progress percentage */}
      <span className="font-sans text-[10px] tracking-widest text-stone-400 tabular-nums">
        {progress}%
      </span>

      {/* Divider */}
      <span className="w-3 h-px bg-stone-300" />

      {/* Reading time */}
      <div className="flex flex-col items-center gap-1">
        <span className="font-sans text-[10px] tracking-widest text-stone-400">
          {readingTime}
        </span>
        <span className="font-sans text-[8px] uppercase tracking-[0.3em] text-stone-300">
          min
        </span>
      </div>

      {/* Divider */}
      <span className="w-3 h-px bg-stone-300" />

      {/* Share button */}
      <button
        onClick={onShareClick}
        className="w-9 h-9 flex items-center justify-center border border-stone-200 text-stone-400 hover:border-stone-900 hover:text-stone-900 transition-all duration-300"
        aria-label={shareLabel}
      >
        <Share2 size={14} />
      </button>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function ArticleContentStone({
  article,
  relatedArticles,
  locale,
  basePath,
  messages,
}: Props) {
  const contentRef = useRef<HTMLElement>(null);
  const articleBodyRef = useRef<HTMLDivElement>(null);
  const progress = useReadingProgress(contentRef);
  const [shareOpen, setShareOpen] = useState(false);
  const [articleUrl, setArticleUrl] = useState('');

  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const articleMessages = (stoneMessages?.article || {}) as Record<string, string>;
  const t = (key: string, fallback: string): string => articleMessages[key] || fallback;

  useSourcesTransform(articleBodyRef);

  useEffect(() => {
    setArticleUrl(window.location.href);
  }, []);

  const readingTime = article.readingTime || 5;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <main className="min-h-screen bg-stone-50">
      {/* ── Top Reading Progress Bar ──────────────────────────────── */}
      <div className="fixed top-0 left-0 right-0 z-50 h-[2px] bg-stone-200/50">
        <div
          className="h-full bg-stone-900 transition-all duration-150 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* ── Floating Sidebar (desktop) ────────────────────────────── */}
      <FloatingSidebar
        progress={progress}
        readingTime={readingTime}
        onShareClick={() => setShareOpen(true)}
        shareLabel={t('shareArticle', 'Share article')}
      />

      {/* ── Share Panel Modal ─────────────────────────────────────── */}
      <SharePanel
        title={article.title}
        url={articleUrl}
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        t={t}
      />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <header className="relative w-full h-[75vh] overflow-hidden">
        {article.featuredImage?.url && (
          <Image
            src={article.featuredImage.url}
            alt={article.featuredImage.alt || article.title}
            fill
            className="object-cover"
            priority
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/20 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-stone-300 mb-6 font-sans">
              <span className="text-white font-medium">
                {article.category?.name || t('defaultCategory', 'Article')}
              </span>
              <span className="w-px h-3 bg-stone-500" />
              <span>{formatDate(article.publishedAt)}</span>
              <span className="w-px h-3 bg-stone-500" />
              <span>{readingTime} {t('minRead', 'min read')}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-tight">
              {article.title}
            </h1>
          </div>
        </div>
      </header>

      {/* ── Toolbar: Back + Share (mobile) ────────────────────────── */}
      <div className="container mx-auto px-6 md:px-12 py-8 flex items-center justify-between">
        <Link
          href={`/${locale}/news`}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 transition-colors font-sans"
        >
          <ArrowLeft size={14} />
          {t('backToJournal', 'Back to Journal')}
        </Link>

        <button
          onClick={() => setShareOpen(true)}
          className="lg:hidden inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 transition-colors font-sans"
        >
          <Share2 size={14} />
          {t('share', 'Share')}
        </button>
      </div>

      {/* ── Article Content ───────────────────────────────────────── */}
      <article ref={contentRef} className="container mx-auto px-6 md:px-12 pb-24">
        <div className="max-w-3xl mx-auto">
          {/* Author Row */}
          <div className="flex items-center gap-4 mb-12 pb-8 border-b border-stone-200">
            {article.author?.avatar && (
              <Image
                src={article.author.avatar}
                alt={article.author.name}
                width={48}
                height={48}
                className="rounded-full"
              />
            )}
            <div>
              <p className="font-serif text-lg text-stone-900">
                {article.author?.name || t('defaultAuthor', 'Team')}
              </p>
              {article.author?.role && (
                <p className="font-sans text-xs uppercase tracking-widest text-stone-500">
                  {article.author.role}
                </p>
              )}
            </div>

            {/* Reading time pill (inline) */}
            <div className="ml-auto flex items-center gap-3 px-4 py-2 border border-stone-200">
              <div className="w-4 h-4 rounded-full border border-stone-300 flex items-center justify-center">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{
                    background: `conic-gradient(#292524 ${progress * 3.6}deg, transparent 0deg)`,
                  }}
                />
              </div>
              <span className="font-sans text-[10px] uppercase tracking-widest text-stone-500">
                {readingTime} {t('minRead', 'min read')}
              </span>
            </div>
          </div>

          {/* Excerpt / Lede */}
          {article.excerpt && (
            <p className="font-serif text-2xl md:text-3xl text-stone-700 leading-relaxed mb-16 italic">
              {article.excerpt}
            </p>
          )}

          {/* Markdown Content with enhanced Stone styling */}
          <div ref={articleBodyRef} className="stone-article-content">
            <MarkdownRenderer
              content={article.content || ''}
              contentFormat={article.contentFormat}
              theme="custom"
              className="stone-prose"
            />
          </div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mt-16 pt-8 border-t border-stone-200">
              <div className="flex flex-wrap gap-3">
                {article.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-4 py-2 text-[10px] uppercase tracking-widest text-stone-500 border border-stone-200 hover:border-stone-900 hover:text-stone-900 transition-colors cursor-default font-sans"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Bottom Share Row */}
          <div className="mt-12 pt-8 border-t border-stone-200 flex items-center justify-between">
            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-stone-400">
              {t('shareThisArticle', 'Share this article')}
            </p>
            <div className="flex items-center gap-4">
              <a
                href={`https://x.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(articleUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-stone-200 text-stone-400 hover:border-stone-900 hover:text-stone-900 transition-all duration-300"
                aria-label={t('shareOnX', 'Share on X')}
              >
                <XIcon size={13} />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-stone-200 text-stone-400 hover:border-stone-900 hover:text-stone-900 transition-all duration-300"
                aria-label={t('shareOnLinkedIn', 'Share on LinkedIn')}
              >
                <LinkedInIcon size={13} />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-stone-200 text-stone-400 hover:border-stone-900 hover:text-stone-900 transition-all duration-300"
                aria-label={t('shareOnFacebook', 'Share on Facebook')}
              >
                <FacebookIcon size={13} />
              </a>
              <button
                onClick={() => setShareOpen(true)}
                className="w-9 h-9 flex items-center justify-center border border-stone-200 text-stone-400 hover:border-stone-900 hover:text-stone-900 transition-all duration-300"
                aria-label={t('moreShareOptions', 'More sharing options')}
              >
                <Share2 size={13} />
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* ── Related Articles ──────────────────────────────────────── */}
      {relatedArticles.length > 0 && (
        <section className="bg-white py-24 border-t border-stone-200">
          <div className="container mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-16">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-px w-6 bg-stone-400" />
                  <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-stone-500">
                    {t('related', 'Related')}
                  </p>
                </div>
                <h2 className="font-serif text-4xl md:text-5xl text-stone-900 italic">
                  {t('continueReading', 'Continue Reading')}
                </h2>
              </div>
              <Link
                href={`/${locale}/news`}
                className="hidden md:flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 transition-colors font-sans"
              >
                {t('viewAll', 'View All')} <ArrowRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  href={`/${locale}/news/${related.slug}`}
                  className="group"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-stone-200 mb-6">
                    {related.featuredImage?.url && (
                      <Image
                        src={related.featuredImage.url}
                        alt={related.featuredImage.alt || related.title}
                        fill
                        className="object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
                      />
                    )}
                  </div>
                  <div className="flex items-baseline justify-between border-b border-stone-200 pb-4 mb-4">
                    <span className="text-xs text-stone-500 uppercase tracking-widest group-hover:text-stone-900 transition-colors font-sans">
                      {related.category?.name || t('defaultCategory', 'Article')}
                    </span>
                    <span className="text-xs text-stone-400 font-serif italic">
                      {formatDate(related.publishedAt)}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl text-stone-900 leading-tight group-hover:italic transition-all duration-300">
                    {related.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Footer CTA ────────────────────────────────────────────── */}
      <section className="relative h-[50vh] overflow-hidden">
        <Image
          src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=2670&auto=format&fit=crop"
          alt={t('contactBackground', 'Contact Background')}
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-stone-900/60" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h2 className="font-serif text-4xl md:text-6xl text-white italic mb-6">
            {t('discoverMore', 'Discover More')}
          </h2>
          <p className="font-sans font-light text-stone-300 mb-8 max-w-xl">
            {t('exploreCollection', 'Explore our curated collection of exceptional properties')}
          </p>
          <Link
            href={`${basePath}/properties`}
            className="px-8 py-4 bg-white text-stone-900 text-xs uppercase tracking-widest font-sans hover:bg-stone-100 transition-colors"
          >
            {t('viewProperties', 'View Properties')}
          </Link>
        </div>
      </section>

      {/* ── Enhanced Stone Article Styles ──────────────────────────── */}
      <style>{`
        /* ── Base Typography ────────────────────────────────────── */
        .stone-prose {
          font-family: var(--font-serif, Georgia, 'Times New Roman', serif);
          font-size: 1.125rem;
          line-height: 1.9;
          color: #57534e;
          max-width: none;
        }

        /* ── Drop Cap ──────────────────────────────────────────── */
        .stone-prose > p:first-of-type::first-letter {
          float: left;
          font-family: var(--font-serif, Georgia, serif);
          font-size: 4.5rem;
          line-height: 0.8;
          padding-right: 0.6rem;
          padding-top: 0.15rem;
          color: #1c1917;
          font-style: italic;
        }

        /* ── Paragraphs ────────────────────────────────────────── */
        .stone-prose p {
          margin-bottom: 1.75rem;
          letter-spacing: 0.01em;
        }

        /* ── Headings ──────────────────────────────────────────── */
        .stone-prose h1 {
          font-family: var(--font-serif, Georgia, serif);
          font-size: 2.75rem;
          line-height: 1.15;
          font-weight: 400;
          font-style: italic;
          color: #1c1917;
          margin-top: 3.5rem;
          margin-bottom: 1.5rem;
          letter-spacing: -0.02em;
        }

        .stone-prose h2 {
          font-family: var(--font-serif, Georgia, serif);
          font-size: 2rem;
          line-height: 1.25;
          font-weight: 400;
          color: #1c1917;
          margin-top: 3rem;
          margin-bottom: 1.25rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #d6d3d1;
          letter-spacing: -0.01em;
        }

        .stone-prose h2::before {
          content: '';
          display: block;
          width: 2rem;
          height: 1px;
          background: #a8a29e;
          margin-bottom: 1.5rem;
        }

        .stone-prose h3 {
          font-family: var(--font-serif, Georgia, serif);
          font-size: 1.5rem;
          line-height: 1.35;
          font-weight: 400;
          font-style: italic;
          color: #1c1917;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
        }

        .stone-prose h4 {
          font-family: var(--font-sans, system-ui, sans-serif);
          font-size: 0.7rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: #78716c;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }

        /* ── Bold & Emphasis ───────────────────────────────────── */
        .stone-prose strong {
          font-weight: 600;
          color: #1c1917;
        }

        .stone-prose em {
          font-style: italic;
          color: #44403c;
        }

        /* ── Links ─────────────────────────────────────────────── */
        .stone-prose a {
          color: #1c1917;
          text-decoration: underline;
          text-underline-offset: 4px;
          text-decoration-color: #d6d3d1;
          transition: text-decoration-color 0.3s ease;
        }
        .stone-prose a:hover {
          text-decoration-color: #1c1917;
        }

        /* ── Blockquotes ───────────────────────────────────────── */
        .stone-prose blockquote {
          position: relative;
          margin: 3rem 0;
          padding: 2rem 0 2rem 2rem;
          border-left: 1px solid #1c1917;
          font-family: var(--font-serif, Georgia, serif);
          font-size: 1.35rem;
          line-height: 1.6;
          font-style: italic;
          color: #1c1917;
        }
        .stone-prose blockquote p {
          margin-bottom: 0;
          color: inherit;
        }
        .stone-prose blockquote cite {
          display: block;
          margin-top: 1rem;
          font-size: 0.75rem;
          font-style: normal;
          font-family: var(--font-sans, system-ui, sans-serif);
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #a8a29e;
        }

        /* ── Lists ─────────────────────────────────────────────── */
        .stone-prose ul {
          list-style: none;
          padding-left: 0;
          margin: 1.5rem 0;
        }
        .stone-prose ul > li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .stone-prose ul > li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.75rem;
          width: 6px;
          height: 1px;
          background: #a8a29e;
        }
        .stone-prose ol {
          list-style: none;
          padding-left: 0;
          margin: 1.5rem 0;
          counter-reset: stone-ol;
        }
        .stone-prose ol > li {
          position: relative;
          padding-left: 2.5rem;
          margin-bottom: 0.75rem;
          counter-increment: stone-ol;
        }
        .stone-prose ol > li::before {
          content: counter(stone-ol, decimal-leading-zero);
          position: absolute;
          left: 0;
          top: 0;
          font-family: var(--font-sans, system-ui, sans-serif);
          font-size: 0.7rem;
          letter-spacing: 0.1em;
          color: #a8a29e;
        }

        /* ── Horizontal Rule ───────────────────────────────────── */
        .stone-prose hr {
          border: none;
          margin: 3rem auto;
          display: flex;
          justify-content: center;
        }
        .stone-prose hr::before {
          content: '◆';
          font-size: 0.5rem;
          letter-spacing: 1.5rem;
          color: #d6d3d1;
        }

        /* ── Code ──────────────────────────────────────────────── */
        .stone-prose code {
          font-family: 'SF Mono', 'Fira Code', monospace;
          font-size: 0.85em;
          background: #f5f5f4;
          color: #1c1917;
          padding: 0.15rem 0.4rem;
          border-radius: 0;
          border: 1px solid #e7e5e4;
        }
        .stone-prose pre {
          background: #1c1917;
          color: #e7e5e4;
          padding: 1.5rem 2rem;
          margin: 2rem 0;
          overflow-x: auto;
          font-size: 0.85rem;
          line-height: 1.7;
          border: none;
        }
        .stone-prose pre code {
          background: none;
          border: none;
          padding: 0;
          color: inherit;
        }

        /* ── Tables ────────────────────────────────────────────── */
        .stone-prose .table-overflow {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          margin: 2.5rem 0;
        }
        .stone-prose table {
          width: 100%;
          border-collapse: collapse;
          font-family: var(--font-sans, system-ui, sans-serif);
          font-size: 0.85rem;
        }
        .stone-prose thead {
          border-bottom: 2px solid #1c1917;
        }
        .stone-prose th {
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-size: 0.7rem;
          color: #1c1917;
          padding: 0.75rem 1rem;
          text-align: left;
        }
        .stone-prose tbody tr {
          border-bottom: 1px solid #e7e5e4;
          transition: background 0.2s ease;
        }
        .stone-prose tbody tr:hover {
          background: #fafaf9;
        }
        .stone-prose td {
          padding: 0.875rem 1rem;
          color: #57534e;
        }

        /* ── Images ────────────────────────────────────────────── */
        .stone-prose img {
          max-width: 100%;
          height: auto;
          margin: 2.5rem 0;
          filter: grayscale(15%);
          transition: filter 0.5s ease;
        }
        .stone-prose img:hover {
          filter: grayscale(0%);
        }

        /* ── Scroll margin for heading anchors ─────────────────── */
        .stone-prose h1[id],
        .stone-prose h2[id],
        .stone-prose h3[id],
        .stone-prose h4[id] {
          scroll-margin-top: 5rem;
        }

        /* ── Sources / References Section ──────────────────────── */
        .stone-sources-section {
          margin-top: 3.5rem;
          padding-top: 2rem;
          border-top: 1px solid #d6d3d1;
        }
        .stone-sources-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        .stone-sources-decor {
          display: block;
          width: 1.5rem;
          height: 1px;
          background: #a8a29e;
        }
        .stone-sources-label {
          font-family: var(--font-sans, system-ui, sans-serif);
          font-size: 0.625rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.25em;
          color: #78716c;
        }
        .stone-sources-links {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .stone-source-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem 0.75rem 0.4rem 0.6rem;
          border: 1px solid #e7e5e4;
          background: #fafaf9;
          text-decoration: none !important;
          transition: all 0.3s ease;
          cursor: pointer;
          max-width: 100%;
        }
        .stone-source-tag:hover {
          border-color: #1c1917;
          background: #fff;
        }
        .stone-source-tag--plain {
          cursor: default;
        }
        .stone-source-tag--plain:hover {
          border-color: #e7e5e4;
          background: #fafaf9;
        }
        .stone-source-num {
          font-family: var(--font-sans, system-ui, sans-serif);
          font-size: 0.55rem;
          letter-spacing: 0.05em;
          color: #a8a29e;
          flex-shrink: 0;
          min-width: 1rem;
        }
        .stone-source-name {
          font-family: var(--font-sans, system-ui, sans-serif);
          font-size: 0.7rem;
          font-weight: 400;
          color: #57534e;
          letter-spacing: 0.01em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: color 0.3s ease;
        }
        .stone-source-tag:hover .stone-source-name {
          color: #1c1917;
        }
        .stone-source-icon {
          flex-shrink: 0;
          color: #d6d3d1;
          display: flex;
          align-items: center;
          transition: color 0.3s ease;
        }
        .stone-source-tag:hover .stone-source-icon {
          color: #1c1917;
        }

        @media (max-width: 640px) {
          .stone-sources-links {
            flex-direction: column;
          }
          .stone-source-tag {
            max-width: 100%;
          }
          .stone-source-name {
            white-space: normal;
          }
        }
      `}</style>
    </main>
  );
}
